import { useState } from "react";
import { User, BookOpen, Search, Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import MeditationIcon from "../icons/MeditationIcon";
import { DropdownItem } from "../dropdown/DropdownItem";
import { Dropdown } from "../dropdown/Dropdown";
import { Link, useNavigate } from "react-router";
import { useAuth } from "../../../modules/auth/application/useAuth";
import { useTherapist } from "../../../context/useTherapist";

import { useSubscription } from "../../../modules/subscription/application/useSubscription";
import { usePlusSubscription } from "../../../modules/subscription/application/usePlusSubscription";
import { useCenter } from "../../../modules/center/application/useCenter";
import { DIRECTORY_PATH } from "../../../modules/public_directory/domain/paths";
import { DIRECTORY_ENABLED } from "../../../modules/public_directory/domain/availability";
import { useBreakpoint } from "../../../hooks/useBreakpoint";

export default function UserDropdown() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const { therapist } = useTherapist();
  const { isPremium } = useSubscription();
  const { data: plus } = usePlusSubscription();
  const { center, isAdmin } = useCenter();
  const { isSmallScreen } = useBreakpoint();
  const { logout } = useAuth();

  const navigate = useNavigate();

  const planLabel = plus?.earlyAdopter
    ? t("subscription.status.earlyAdopterPlan")
    : isPremium
      ? // Plus or Max, from whatever pays for it — a centre seat included.
        t(therapist?.plan_tier === "max" ? "subscription.max.title" : "subscription.plus.title")
      : t("subscription.free.title");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  function toggleDropdown() {
    setIsOpen(!isOpen);
  }

  function closeDropdown() {
    setIsOpen(false);
  }

  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const docsUrl = `https://alchely.com/${lang}/docs/`;
  // A public-site resource a therapist reaches for mid-session — grounding at
  // the start of one, or homework between them. Its own tab, so the session
  // they are in stays where it is.
  const meditationTimerUrl = `https://alchely.com/${lang}/resources/meditation-timer/`;

  // ... existing code ...

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        data-tour="user-profile"
        className="flex items-center text-gray-700 dropdown-toggle dark:text-gray-400"
      >

        <div className="text-right mr-3 hidden md:block">
          <span className="block font-medium text-theme-sm text-content-primary">
            {therapist
              ? [therapist.first_name, therapist.last_name].filter(Boolean).join(" ")
              : t("common.loading")}
          </span>
          {/* What the account is on. An early adopter holds Plus for ever and
              is billed for nothing, so calling that "Plus" sends them looking
              for a subscription that does not exist. */}
          <span className={`block text-xs font-semibold uppercase tracking-[0.08em] ${isPremium ? "zen-text-gradient" : "text-content-secondary"}`}>
            {planLabel}
          </span>
        </div>
        <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center border border-gray-200 dark:border-white/10 overflow-hidden shadow-sm">
          {therapist?.photo_url ? (
            <img src={therapist.photo_url} alt="Profile" className="h-full w-full object-cover" />
          ) : (
            <User className="h-5 w-5 text-content-tertiary" />
          )}
        </div>
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className="absolute right-0 mt-[17px] flex w-[260px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark"
      >
        <div>
          <span className="block font-medium text-gray-700 text-theme-sm dark:text-gray-400">
            {therapist
              ? [therapist.first_name, therapist.last_name].filter(Boolean).join(" ")
              : t("common.loading")}
          </span>
          {/* The plan is already shown in the header, beside the name. */}
          <span className="mt-0.5 block text-theme-xs text-content-tertiary">
            {therapist?.email ?? ""}
          </span>
          {center && (
            <Link
              to="/profile?tab=center"
              onClick={closeDropdown}
              className="mt-1 flex items-center gap-1.5 text-theme-xs font-medium text-content-secondary hover:text-content-primary"
            >
              <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{center.name}</span>
            </Link>
          )}
        </div>

        <ul className="flex flex-col gap-1 pt-4 pb-3 border-b border-border-subtle">
          <li>
            <DropdownItem
              onItemClick={closeDropdown}
              tag="a"
              to="/profile"
              className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
            >
              <svg
                className="fill-gray-500 group-hover:fill-gray-700 dark:fill-gray-400 dark:group-hover:fill-gray-300"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 14.1526 4.3002 16.1184 5.61936 17.616C6.17279 15.3096 8.24852 13.5955 10.7246 13.5955H13.2746C15.7509 13.5955 17.8268 15.31 18.38 17.6167C19.6996 16.119 20.5 14.153 20.5 12C20.5 7.30558 16.6944 3.5 12 3.5ZM17.0246 18.8566V18.8455C17.0246 16.7744 15.3457 15.0955 13.2746 15.0955H10.7246C8.65354 15.0955 6.97461 16.7744 6.97461 18.8455V18.856C8.38223 19.8895 10.1198 20.5 12 20.5C13.8798 20.5 15.6171 19.8898 17.0246 18.8566ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM11.9991 7.25C10.8847 7.25 9.98126 8.15342 9.98126 9.26784C9.98126 10.3823 10.8847 11.2857 11.9991 11.2857C13.1135 11.2857 14.0169 10.3823 14.0169 9.26784C14.0169 8.15342 13.1135 7.25 11.9991 7.25ZM8.48126 9.26784C8.48126 7.32499 10.0563 5.75 11.9991 5.75C13.9419 5.75 15.5169 7.32499 15.5169 9.26784C15.5169 11.2107 13.9419 12.7857 11.9991 12.7857C10.0563 12.7857 8.48126 11.2107 8.48126 9.26784Z"
                  fill=""
                />
              </svg>
              {t("common.profile")}
            </DropdownItem>
          </li>
          {/* On a phone there is no sidebar, and the bottom bar has no slot
              left: an administrator reaches the centre from here, next to
              their profile. On larger screens the sidebar already has it. */}
          {isSmallScreen && isAdmin && (
          <li>
            <DropdownItem
              onItemClick={closeDropdown}
              tag="a"
              to="/center"
              className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
            >
              <Building2 className="w-5 h-5 text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-300" />
              {t("sidebar.centerManagement")}
            </DropdownItem>
          </li>
          )}
          {/* Hidden while the catalogue is closed: a link to a page that
              lists nobody reads as a broken product, not as a beta. */}
          {DIRECTORY_ENABLED && (
          <li>
            {/* The directory is an anonymous surface outside AppLayout, so it
                opens in its own tab rather than dropping the therapist out of
                the app chrome with no way back. */}
            <a
              href={DIRECTORY_PATH}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeDropdown}
              className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
            >
              <Search className="w-5 h-5 text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-300" />
              {t("common.therapistCatalog")}
            </a>
          </li>
          )}
          <li>
            <a
              href={docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeDropdown}
              className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
            >
              <BookOpen className="w-5 h-5 text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-300" />
              {t("common.documentation")}
            </a>
          </li>
          <li>
            <a
              href={meditationTimerUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeDropdown}
              className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
            >
              <MeditationIcon className="w-5 h-5 text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-300" />
              {t("common.meditationTimer")}
            </a>
          </li>
          <li>
            <DropdownItem
              onItemClick={closeDropdown}
              tag="a"
              to="/support"
              className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
            >
              <svg
                className="fill-gray-500 group-hover:fill-gray-700 dark:fill-gray-400 dark:group-hover:fill-gray-300"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"
                  fill=""
                />
              </svg>
              {t("common.support")}
            </DropdownItem>
          </li>
        </ul>
        <Link
          to="/login"
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2 mt-3 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
        >
          <svg
            className="fill-gray-500 group-hover:fill-gray-700 dark:group-hover:fill-gray-300"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M15.1007 19.247C14.6865 19.247 14.3507 18.9112 14.3507 18.497L14.3507 14.245H12.8507V18.497C12.8507 19.7396 13.8581 20.747 15.1007 20.747H18.5007C19.7434 20.747 20.7507 19.7396 20.7507 18.497L20.7507 5.49609C20.7507 4.25345 19.7433 3.24609 18.5007 3.24609H15.1007C13.8581 3.24609 12.8507 4.25345 12.8507 5.49609V9.74501L14.3507 9.74501V5.49609C14.3507 5.08188 14.6865 4.74609 15.1007 4.74609L18.5007 4.74609C18.9149 4.74609 19.2507 5.08188 19.2507 5.49609L19.2507 18.497C19.2507 18.9112 18.9149 19.247 18.5007 19.247H15.1007ZM3.25073 11.9984C3.25073 12.2144 3.34204 12.4091 3.48817 12.546L8.09483 17.1556C8.38763 17.4485 8.86251 17.4487 9.15549 17.1559C9.44848 16.8631 9.44863 16.3882 9.15583 16.0952L5.81116 12.7484L16.0007 12.7484C16.4149 12.7484 16.7507 12.4127 16.7507 11.9984C16.7507 11.5842 16.4149 11.2484 16.0007 11.2484L5.81528 11.2484L9.15585 7.90554C9.44864 7.61255 9.44847 7.13767 9.15547 6.84488C8.86248 6.55209 8.3876 6.55226 8.09481 6.84525L3.52309 11.4202C3.35673 11.5577 3.25073 11.7657 3.25073 11.9984Z"
              fill=""
            />
          </svg>
          {t("common.signOut")}
        </Link>
      </Dropdown>
    </div>
  );
}
